import { api } from './api';
import { getLocalDate } from '../utils/date-utils';
import type { CanvasResponse } from '@xdface-vault/shared';

export interface CanvasWithUrls extends CanvasResponse {
  dataUrl: string;
  uploadUrl: string;
}

export interface ListCanvasesResponse {
  data: CanvasResponse[];
  pagination: { cursor?: string; hasMore: boolean };
}

export interface CreateCanvasInput {
  title: string;
  tags?: string[];
  initialData?: object;
}

export interface UpdateCanvasMetaInput {
  title?: string;
  tags?: string[];
  expectedVersion: number;
}

export const canvasApi = {
  async listCanvases(): Promise<CanvasResponse[]> {
    const res = await api.get<ListCanvasesResponse>('/canvases');
    return res.data;
  },

  async getCanvas(id: string): Promise<CanvasWithUrls> {
    return api.get<CanvasWithUrls>(`/canvases/${id}`);
  },

  async createCanvas(input: CreateCanvasInput): Promise<CanvasWithUrls> {
    return api.post<CanvasWithUrls>('/canvases', { ...input, localDate: getLocalDate() });
  },

  async updateCanvasMeta(id: string, input: UpdateCanvasMetaInput): Promise<CanvasResponse> {
    return api.put<CanvasResponse>(`/canvases/${id}`, input);
  },

  async saveCanvasData(uploadUrl: string, data: object): Promise<void> {
    const response = await fetch(uploadUrl, {
      method: 'PUT',
      body: JSON.stringify(data),
      headers: { 'Content-Type': 'application/json' },
    });
    if (!response.ok) {
      throw new Error(`Failed to save canvas data: ${response.statusText}`);
    }
  },

  async deleteCanvas(id: string): Promise<void> {
    await api.delete(`/canvases/${id}`);
  },

  async shareCanvas(
    id: string,
    ttlMinutes: 15 | 30 | 60 | 90 | 120,
  ): Promise<{
    token: string;
    shareUrl: string;
    accessCode: string;
    expiresAt: string;
    ttlMinutes: number;
  }> {
    return api.post(`/canvases/${id}/share`, { ttlMinutes, localDate: getLocalDate() });
  },

  async deleteCanvasShare(id: string): Promise<void> {
    await api.delete(`/canvases/${id}/share`);
  },

  async getPublicCanvas(
    token: string,
    guestToken?: string,
    deviceId?: string,
  ): Promise<CanvasWithUrls & { requiresCode?: boolean }> {
    const headers: Record<string, string> = {};
    if (guestToken) {
      headers.Authorization = `Bearer ${guestToken}`;
    }
    if (deviceId) {
      headers['X-Device-Id'] = deviceId;
    }
    const res = await fetch(`${import.meta.env.VITE_API_URL ?? ''}/public/canvas/${token}`, {
      headers,
    });
    const json = await res.json().catch(() => ({ message: 'Unknown error' }));
    if (!res.ok) {
      throw Object.assign(new Error(json.message || 'Request failed'), {
        status: res.status,
        body: json,
      });
    }
    if (json && typeof json === 'object' && 'success' in json && 'data' in json) {
      return json.data as CanvasWithUrls;
    }
    return json as CanvasWithUrls;
  },

  async verifyCanvasCode(
    token: string,
    code: string,
    deviceId?: string,
  ): Promise<{ data: CanvasWithUrls; guestToken: string }> {
    const res = await fetch(`${import.meta.env.VITE_API_URL ?? ''}/public/canvas/${token}/verify`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code, deviceId }),
    });
    const json = await res.json().catch(() => ({ message: 'Unknown error' }));
    if (!res.ok) {
      throw new Error((json as { message?: string }).message || 'Request failed');
    }
    return json as { data: CanvasWithUrls; guestToken: string };
  },
};
